import React, { useRef, useEffect, useState } from 'react'
import { string, node, func, oneOfType, bool } from 'prop-types'
import { css } from '@emotion/react'
import { navigate } from 'gatsby'

import ChevronRightIcon from '@/assets/icons/chevron-nav-right.inline.svg'
import { canUseWindow, getLocalStorageValue } from '@/utils/browser'
import { KEY_CODES } from '@/constants'
import { isIframeVersion } from '@/utils'

const collapseCss = (theme) => css`
  .collapse__input {
    display: none;
  }

  .collapse__content {
    display: none;
  }

  .collapse__icon {
    display: flex;
    width: 40px;
    margin-left: 5px;
    align-items: center;
    justify-content: flex-end;

    > svg > path {
      opacity: 1;
      fill: ${theme.colors.gray900};
    }
  }

  .collapse__label {
    cursor: pointer;
    display: flex;
    align-items: center;
    padding: 8px;
    user-select: none;
  }

  .collapse__label-wrapper:focus > .collapse__label {
    outline: -webkit-focus-ring-color auto 5px;
  }

  .collapse__label-wrapper:focus,
  .collapse__label:focus {
    outline: none;
  }

  .collapse__input:checked + .collapse__label-wrapper + .collapse__content {
    display: block;
  }

  .collapse__input:checked + .collapse__label-wrapper .collapse__icon > svg {
    transform: rotate(90deg);
  }
`

const getValueFromSessionStorage = (key) => {
  if (canUseWindow) {
    const value = window.sessionStorage.getItem(key)
    return JSON.parse(value) || false
  }

  return false
}

const Collapse = ({
  tag: Tag = 'div',
  label,
  content,
  storageKey,
  labelComponent,
  isOpenOnInit,
  sectionKey,
  sectionLink,
  hasFilters,
  noLabelNav,
  ...rest
}) => {
  let toggleTimeout
  const inputRef = useRef()
  const isOpenInitialValue =
    getValueFromSessionStorage(storageKey) || isOpenOnInit
  const [isContentVisible, setContentVisibility] = useState(isOpenInitialValue)

  const isSidebarChevron = isIframeVersion && !hasFilters

  const toggleOpen = (e) => {
    if (storageKey && !noLabelNav && canUseWindow) {
      toggleTimeout = setTimeout(
        window.sessionStorage.setItem(storageKey, e.target.checked)
      )
    }
  }

  const handleKeyPress = (e) => {
    switch (e.charCode) {
      case KEY_CODES.SPACEBAR:
      case KEY_CODES.ENTER:
        inputRef.current.click()
        break

      default:
    }
  }

  const handleClick = (e) => {
    // Workaround to show categories when clicking on chevrons in iFrame-type sidebar
    if (isSidebarChevron && !getLocalStorageValue(sectionKey) && !noLabelNav) {
      navigate(sectionLink)
    }
    setContentVisibility(e.target.checked)
    toggleOpen(e)
  }

  useEffect(() => clearTimeout(toggleTimeout))

  return (
    <Tag className="collapse" css={collapseCss} {...rest}>
      <input
        id={storageKey}
        className="collapse__input"
        type="checkbox"
        onClick={handleClick}
        defaultChecked={isOpenInitialValue}
        ref={inputRef}
      />

      {labelComponent ? (
        <div className="collapse__label-wrapper">
          {typeof labelComponent === 'function'
            ? labelComponent({ inputRef, isContentVisible })
            : labelComponent}
        </div>
      ) : (
        <label
          onKeyPress={handleKeyPress}
          htmlFor={storageKey}
          className="collapse__label-wrapper"
          tabIndex="0"
        >
          <div className="collapse__label" tabIndex="-1">
            {typeof label === 'function'
              ? label({ inputRef, isContentVisible })
              : label}

            <div className="collapse__icon">
              <ChevronRightIcon />
            </div>
          </div>
        </label>
      )}
      <div className="collapse__content">{content}</div>
    </Tag>
  )
}

Collapse.propTypes = {
  tag: node,
  label: oneOfType([node, func]),
  labelComponent: oneOfType([node, func]),
  content: node,
  storageKey: string,
  isOpenOnInit: bool,
  sectionKey: string,
  sectionLink: string,
  hasFilters: bool,
  noLabelNav: bool,
}

export default Collapse
